import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, imageUrl, formatRupiah, errorMessage } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ArrowLeft, Trash2, Plus, Minus, ShoppingBag, Package, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CartPage() {
    const navigate = useNavigate();
    const { items, updateQty, removeItem, clear, total, count, keyOf } = useCart();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        customer_name: "",
        customer_phone: "",
        customer_address: "",
        notes: "",
    });

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (items.length === 0) return;
        if (!form.customer_name.trim()) return toast.error("Nama harus diisi");
        if (!form.customer_phone.trim()) return toast.error("Nomor WhatsApp harus diisi");
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                items: items.map((i) => ({
                    product_id: i.product_id,
                    varian: i.varian || "",
                    sub_varian: i.sub_varian || "",
                    quantity: i.quantity,
                })),
            };
            const { data } = await api.post("/orders", payload);
            clear();
            toast.success(`Order ${data.order_no} berhasil dikirim!`);
            navigate(`/order-success/${data.order_no}`);
        } catch (err) {
            toast.error(errorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (items.length === 0) {
        return (
            <div data-testid="cart-page-empty" className="mx-auto max-w-2xl px-5 py-20 text-center">
                <div className="mx-auto h-20 w-20 grid place-items-center rounded-full bg-secondary mb-4">
                    <ShoppingBag size={32} className="text-muted-foreground" strokeWidth={1.5} />
                </div>
                <h1 className="font-heading text-2xl font-semibold">Keranjang Masih Kosong</h1>
                <p className="mt-2 text-sm text-muted-foreground">Cari produk lalu klik "Tambah ke Keranjang"</p>
                <Link
                    to="/"
                    data-testid="back-to-catalog-btn"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                    <ArrowLeft size={14} /> Lihat Katalog
                </Link>
            </div>
        );
    }

    return (
        <div data-testid="cart-page" className="mx-auto max-w-4xl px-5 py-8">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="cart-back-link">
                <ArrowLeft size={14} /> Lanjut Belanja
            </Link>
            <div className="mt-3 flex items-baseline gap-3">
                <h1 className="font-heading text-3xl font-semibold tracking-tight">Keranjang</h1>
                <span className="text-sm text-muted-foreground" data-testid="cart-item-count">{count} item</span>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_360px]">
                {/* Items list */}
                <div className="space-y-3" data-testid="cart-items-list">
                    {items.map((it) => {
                        const k = keyOf(it);
                        const label = [it.varian, it.sub_varian].filter(Boolean).join(" · ");
                        return (
                            <div key={k} data-testid={`cart-item-${k}`} className="flex gap-3 rounded-xl border border-border bg-white p-3">
                                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md bg-secondary">
                                    {it.image_path ? (
                                        <img src={imageUrl(it.image_path)} alt={it.name} className="h-full w-full object-cover" />
                                    ) : (
                                        <div className="h-full w-full grid place-items-center text-muted-foreground"><Package size={24} strokeWidth={1.25} /></div>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0 flex flex-col gap-1">
                                    <div className="text-sm font-medium line-clamp-2">{it.name}</div>
                                    {label && <div className="text-xs text-muted-foreground">{label}</div>}
                                    <div className="text-sm text-primary font-semibold">{formatRupiah(it.unit_price)}</div>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <button
                                        onClick={() => removeItem(k)}
                                        data-testid={`cart-remove-${k}`}
                                        title="Hapus"
                                        className="text-muted-foreground hover:text-destructive p-1"
                                    >
                                        <Trash2 size={16} strokeWidth={1.75} />
                                    </button>
                                    <div className="inline-flex items-center gap-1">
                                        <button onClick={() => updateQty(k, it.quantity - 1)} className="grid h-7 w-7 place-items-center rounded-full border border-border hover:bg-secondary" data-testid={`cart-dec-${k}`}>
                                            <Minus size={12} />
                                        </button>
                                        <span className="w-7 text-center text-sm font-medium" data-testid={`cart-qty-${k}`}>{it.quantity}</span>
                                        <button onClick={() => updateQty(k, it.quantity + 1)} className="grid h-7 w-7 place-items-center rounded-full border border-border hover:bg-secondary" data-testid={`cart-inc-${k}`}>
                                            <Plus size={12} />
                                        </button>
                                    </div>
                                    <div className="text-sm font-semibold" data-testid={`cart-line-total-${k}`}>{formatRupiah(it.unit_price * it.quantity)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Checkout form */}
                <form onSubmit={submit} className="space-y-4 rounded-xl border border-border bg-white p-5 h-fit sticky top-20" data-testid="checkout-form">
                    <h2 className="font-heading text-lg font-semibold">Data Pemesan</h2>
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Nama Lengkap *</span>
                        <input data-testid="checkout-name-input" required value={form.customer_name} onChange={onChange("customer_name")} placeholder="Nama Anda" className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-accent" />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Nomor WhatsApp *</span>
                        <input data-testid="checkout-phone-input" required value={form.customer_phone} onChange={onChange("customer_phone")} placeholder="0812-3456-7890" inputMode="tel" className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-accent" />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Alamat (opsional)</span>
                        <textarea data-testid="checkout-address-input" rows={2} value={form.customer_address} onChange={onChange("customer_address")} placeholder="Alamat lengkap" className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-accent" />
                    </label>
                    <label className="block">
                        <span className="label-eyebrow text-muted-foreground">Catatan (opsional)</span>
                        <textarea data-testid="checkout-notes-input" rows={2} value={form.notes} onChange={onChange("notes")} placeholder="Pesan untuk toko" className="mt-2 w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-accent" />
                    </label>

                    <div className="border-t border-border pt-4">
                        <div className="flex items-baseline justify-between mb-3">
                            <span className="text-sm text-muted-foreground">Total Order</span>
                            <span data-testid="cart-grand-total" className="font-heading text-2xl font-semibold text-primary">{formatRupiah(total)}</span>
                        </div>
                        <button
                            data-testid="checkout-submit-btn"
                            type="submit"
                            disabled={submitting}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground hover:opacity-90 disabled:opacity-50"
                        >
                            {submitting ? <Loader2 className="animate-spin" size={14} /> : <ShoppingBag size={14} />}
                            Buat Order
                        </button>
                        <p className="mt-2 text-[11px] text-center text-muted-foreground">Order akan dikirim ke toko via WhatsApp untuk konfirmasi</p>
                    </div>
                </form>
            </div>
        </div>
    );
}
